import React, { useState, useEffect, useCallback, useMemo } from "react";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import {
    Eye, RefreshCw, X, Search, Plus, Trash2, Save,
    FileText, Download, ShoppingCart, Users, CalendarDays,
    Building2, CheckSquare, Square, ChevronDown, ChevronUp,
    ClipboardList, Link2
} from "lucide-react";

// ── Constants ─────────────────────────────────────────────────────────────────
const PAGE_SIZE = 10;
const UOM_OPTIONS = ["KGS", "PCS", "ROLLS", "BALES", "BAGS"];

const gradHdr = {
    background: "linear-gradient(135deg, #0c1445 0%, #1a2f7a 50%, #1e40af 100%)",
};

// ── BLUE THEME ────────────────────────────────────────────────────────────────
const gradBlue = {
    background: "linear-gradient(135deg, #0c1445 0%, #1a2f7a 50%, #1e40af 100%)",
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const esc = (s) =>
    String(s ?? "").replace(/[&<>"']/g, (c) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
    );

const fmt = (n) =>
    Number(n)
        ? Number(n).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "0.00";

const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

// Field accessors for SO rows from API
const f = {
    id: (r) => r.id || r.Id || 0,
    soNo: (r) => r.soNo || r.SoNo || "",
    soPrefix: (r) => r.soPrefix || r.SoPrefix || "VS",
    soDate: (r) => r.soDate || r.SoDate || "",
    orderedBy: (r) => r.orderedBy || r.OrderedBy || "",
    address: (r) => r.address || r.Address || "",
    salesman: (r) => r.salesman || r.Salesman || "",
    refNo: (r) => r.refNo || r.RefNo || "",
    soRef: (r) => r.soRef || r.SoRef || "",
    remarks: (r) => r.remarks || r.Remarks || "",
    preparedBy: (r) => r.preparedBy || r.PreparedBy || "",
    checkedBy: (r) => r.checkedBy || r.CheckedBy || "",
    receivedBy: (r) => r.receivedBy || r.ReceivedBy || "",
    createdBy: (r) => r.createdBy || r.CreatedBy || "",
    items: (r) => r.items || r.Items || [],
    refs: (r) => r.scrapRefs || r.ScrapRefs || [],
};

// Field accessors for Scrap Loading rows
const fScrap = {
    id: (r) => r.id || r.Id || 0,
    controlNo: (r) => r.controlNo || r.ControlNo || "",
    formDate: (r) => r.formDate || r.FormDate || "",
    customerName: (r) => r.customerName || r.CustomerName || "",
    preparedBy: (r) => r.preparedBy || r.PreparedBy || "",
    checkedBy: (r) => r.checkedBy || r.CheckedBy || "",
    receivedBy: (r) => r.receivedBy || r.ReceivedBy || "",
    remarks: (r) => r.remarks || r.Remarks || "",
    items: (r) => r.items || r.Items || [],
};

const EMPTY_FORM = () => ({
    soNo: "",
    soPrefix: "VS",
    soDate: new Date().toISOString().split("T")[0],
    orderedBy: "",
    address: "",
    salesman: "",
    refNo: "",
    soRef: "",
    remarks: "",
    preparedBy: "",
    checkedBy: "",
    receivedBy: "",
    items: [],
    scrapControlNos: [],
});

// ── Compute totals ────────────────────────────────────────────────────────────
const computeTotals = (items) => {
    const totalSales = items.reduce((sum, i) => {
        const amt = parseFloat(i.amount) ||
            (parseFloat(i.qty) * parseFloat(i.unitPrice)) || 0;
        return sum + amt;
    }, 0);
    return { totalSales, discount: 0, totalPayable: totalSales };
};

// ── PDF Export ────────────────────────────────────────────────────────────────
const exportSOPDF = async (soData) => {
    if (!window.html2pdf) {
        await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
            s.onload = resolve; s.onerror = reject;
            document.head.appendChild(s);
        });
    }

    const soNo = f.soNo(soData);
    const prefix = f.soPrefix(soData);
    const items = f.items(soData);
    const { totalSales, discount, totalPayable } = computeTotals(items);

    const MIN_ROWS = 8;
    const displayItems = [
        ...items,
        ...Array.from({ length: Math.max(0, MIN_ROWS - items.length) }, () => ({})),
    ];

    const rowsHTML = displayItems.map((item, i) => {
        const bg = i % 2 === 0 ? "#fff" : "#f0fdf4";
        const amt = parseFloat(item.amount) ||
            (parseFloat(item.qty) * parseFloat(item.unitPrice)) || 0;
        return `<tr style="background:${bg};border-bottom:0.5px solid #d1d5db">
            <td style="padding:3px 6px;text-align:right;border-right:0.5px solid #e5e7eb;font-size:9px;font-weight:600">${esc(String(item.qty ?? ""))}</td>
            <td style="padding:3px 5px;text-align:center;border-right:0.5px solid #e5e7eb;font-size:8px;color:#065f46">${esc(item.uom || "")}</td>
            <td style="padding:3px 5px;text-align:center;border-right:0.5px solid #e5e7eb;font-size:8px;color:#374151">${esc(item.itemCode || "")}</td>
            <td style="padding:3px 7px;border-right:0.5px solid #e5e7eb;font-size:9px;font-weight:${item.description ? "700" : "400"}">${esc(item.description || "")}</td>
            <td style="padding:3px 6px;text-align:right;border-right:0.5px solid #e5e7eb;font-size:9px">${item.unitPrice ? fmt(item.unitPrice) : ""}</td>
            <td style="padding:3px 6px;text-align:right;font-size:9px;font-weight:700">${amt ? fmt(amt) : ""}</td>
        </tr>`;
    }).join("");

    const soDate = f.soDate(soData);
    const html = `
    <div style="font-family:'Times New Roman',Times,serif;width:160mm;padding:5mm 7mm;background:#fff;border:1px solid #ccc">
        <div style="text-align:center;padding-bottom:5px;border-bottom:2px solid #000;margin-bottom:6px">
            <div style="font-size:11px;font-weight:900;letter-spacing:0.08em;text-transform:uppercase">MEGA SOFT HYGIENIC PRODUCTS, INC.</div>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
            <div style="font-size:19px;font-weight:900;font-style:italic">SALES ORDER</div>
            <div style="display:flex;align-items:center;gap:4px">
                <span style="font-size:9px;font-weight:700;color:#555">SO No</span>
                <span style="font-size:9px;font-weight:900;color:#166534">${esc(prefix)}</span>
                <span style="font-size:20px;font-weight:900;color:#166534;letter-spacing:2px;font-family:monospace">${esc(soNo)}</span>
            </div>
        </div>
        <div style="display:flex;justify-content:space-between;margin-bottom:6px;border-bottom:0.5px solid #ccc;padding-bottom:4px">
            <table style="border-collapse:collapse;font-size:9px">
                <tr><td style="padding:1px 8px 1px 0;color:#555;font-weight:700">Ordered by:</td><td style="font-weight:700;border-bottom:1px solid #000;min-width:120px">${esc(f.orderedBy(soData))}</td></tr>
                <tr><td style="padding:1px 8px 1px 0;color:#555;font-weight:700">Address:</td>  <td style="border-bottom:1px dotted #ccc;min-width:120px">${esc(f.address(soData))}</td></tr>
                <tr><td style="padding:1px 8px 1px 0;color:#555;font-weight:700">Salesman:</td> <td style="border-bottom:1px dotted #ccc;min-width:120px">${esc(f.salesman(soData))}</td></tr>
            </table>
            <div style="font-size:9px;text-align:right;padding-top:2px">
                <span style="color:#555;font-weight:700">Date: </span>
                <span style="font-weight:700">${soDate ? new Date(soDate).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : ""}</span>
            </div>
        </div>
        <table style="width:100%;border-collapse:collapse;border:1px solid #999;margin-bottom:6px">
            <thead>
                <tr style="background:#1e3a8a">
                    <th style="padding:4px 6px;color:#fff;font-size:8px;text-align:right;width:50px;border-right:0.5px solid rgba(255,255,255,0.3)">Qty</th>
                    <th style="padding:4px 5px;color:#fff;font-size:8px;text-align:center;width:36px;border-right:0.5px solid rgba(255,255,255,0.3)">UOM</th>
                    <th style="padding:4px 5px;color:#fff;font-size:8px;text-align:center;width:65px;border-right:0.5px solid rgba(255,255,255,0.3)">Item Code</th>
                    <th style="padding:4px 7px;color:#fff;font-size:8px;text-align:left;border-right:0.5px solid rgba(255,255,255,0.3)">DESCRIPTION</th>
                    <th style="padding:4px 6px;color:#fff;font-size:8px;text-align:right;width:65px;border-right:0.5px solid rgba(255,255,255,0.3)">Unit Price</th>
                    <th style="padding:4px 6px;color:#fff;font-size:8px;text-align:right;width:70px">Amount</th>
                </tr>
            </thead>
            <tbody>${rowsHTML}</tbody>
        </table>
        <div style="display:flex;justify-content:flex-end;margin-bottom:8px">
            <table style="border-collapse:collapse;font-size:9px;min-width:180px">
                <tr><td style="padding:2px 10px 2px 0;color:#555;font-weight:700;text-align:right">TOTAL SALES:</td><td style="text-align:right;font-weight:700;border-bottom:0.5px solid #e5e7eb;min-width:90px">${fmt(totalSales)}</td></tr>
                <tr><td style="padding:2px 10px 2px 0;color:#555;font-weight:700;text-align:right">DISCOUNT:</td><td style="text-align:right;border-bottom:0.5px solid #e5e7eb">${discount ? fmt(discount) : "—"}</td></tr>
                <tr style="background:#f0fdf4"><td style="padding:3px 10px 3px 0;color:#166534;font-weight:900;text-align:right">TOTAL AMOUNT PAYABLE:</td><td style="text-align:right;font-weight:900;color:#166534;font-size:10px;border-bottom:2px solid #166534">${fmt(totalPayable)}</td></tr>
            </table>
        </div>
        <div style="font-size:8px;color:#555;margin-bottom:8px">
            <span style="font-weight:700">Ref. # </span>${esc(f.refNo(soData) || "—")}&nbsp;&nbsp;
            <span style="font-weight:700">SO # </span>${esc(f.soRef(soData) || "—")}
        </div>
        <div style="display:flex;border-top:0.5px solid #e5e7eb;padding-top:6px">
            ${[["PREPARED BY", f.preparedBy(soData)], ["Confirmed by", f.checkedBy(soData)], ["Received by", f.receivedBy(soData)]].map(([label, val]) =>
        `<div style="flex:1;text-align:center">
                    <div style="font-size:9px;font-weight:900;border-bottom:1px solid #000;padding-bottom:2px;min-height:18px;display:inline-block;min-width:90px">${esc(val || "")}</div>
                    <div style="font-size:6.5px;color:#94a3b8;margin-top:2px;font-style:italic">${label}:</div>
                </div>`).join("")}
        </div>
        <div style="margin-top:5px;border-top:0.5px solid #e5e7eb;text-align:center;padding-top:3px">
            <p style="font-size:6px;color:#94a3b8;font-style:italic;margin:0">This document is a property of Mega Soft Hygienic Products, Inc. Unauthorized reproduction is strictly prohibited.</p>
        </div>
    </div>`;

    const el = document.createElement("div");
    el.innerHTML = html;
    document.body.appendChild(el);
    try {
        await window.html2pdf(el, {
            margin: [4, 4, 4, 4],
            filename: `SalesOrder_${prefix}${soNo}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, logging: false },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        });
    } finally {
        document.body.removeChild(el);
    }
};

// ── SO Document Preview ───────────────────────────────────────────────────────
const SalesOrderDocument = ({ data }) => {
    const items = f.items(data);
    const { totalSales, discount, totalPayable } = computeTotals(items);
    const MIN_ROWS = 8;
    const displayItems = [
        ...items,
        ...Array.from({ length: Math.max(0, MIN_ROWS - items.length) }, () => ({})),
    ];

    return (
        <div style={{ fontFamily: "'Times New Roman', Times, serif", background: "#fff", border: "1px solid #ccc" }}>
            <div style={{ textAlign: "center", padding: "10px 24px 6px", borderBottom: "2.5px solid #000" }}>
                <div style={{ fontSize: "13px", fontWeight: "900", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                    MEGA SOFT HYGIENIC PRODUCTS, INC.
                </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 24px 6px" }}>
                <div style={{ fontSize: "22px", fontWeight: "900", fontStyle: "italic", color: "#000" }}>SALES ORDER</div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#555" }}>SO No</span>
                    <span style={{ fontSize: "11px", fontWeight: "900", color: "#166534" }}>{f.soPrefix(data)}</span>
                    <span style={{ fontSize: "22px", fontWeight: "900", color: "#166534", letterSpacing: "2px", fontFamily: "monospace" }}>
                        {f.soNo(data)}
                    </span>
                </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "0 24px 8px", borderBottom: "0.5px solid #ccc" }}>
                <table style={{ borderCollapse: "collapse", fontSize: "11px", fontFamily: "Arial, sans-serif" }}>
                    <tbody>
                        {[
                            ["Ordered by:", f.orderedBy(data)],
                            ["Address:", f.address(data)],
                            ["Salesman:", f.salesman(data)],
                        ].map(([label, val]) => (
                            <tr key={label}>
                                <td style={{ padding: "2px 10px 2px 0", color: "#555", fontWeight: "700", whiteSpace: "nowrap" }}>{label}</td>
                                <td style={{ fontWeight: "700", color: "#000", borderBottom: "1px solid #000", minWidth: "160px", paddingBottom: "1px" }}>
                                    {val || "\u00A0"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div style={{ textAlign: "right", fontSize: "11px", paddingTop: "4px", fontFamily: "Arial, sans-serif" }}>
                    <span style={{ color: "#555", fontWeight: "700" }}>Date: </span>
                    <span style={{ fontWeight: "700" }}>
                        {f.soDate(data) ? new Date(f.soDate(data)).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "\u00A0"}
                    </span>
                </div>
            </div>
            <div style={{ padding: "8px 24px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #999" }}>
                    <thead>
                        <tr style={{ background: "#1e3a8a" }}>
                            {[
                                { h: "Qty", align: "right", w: "55px" },
                                { h: "UOM", align: "center", w: "40px" },
                                { h: "Item Code", align: "center", w: "90px" },
                                { h: "DESCRIPTION", align: "left" },
                                { h: "Unit Price", align: "right", w: "80px" },
                                { h: "Amount", align: "right", w: "85px" },
                            ].map(({ h, align, w }) => (
                                <th key={h} style={{ padding: "5px 7px", color: "#fff", fontSize: "9px", fontWeight: "700", textTransform: "uppercase", textAlign: align, width: w || "auto", borderRight: "0.5px solid rgba(255,255,255,0.3)", fontFamily: "Arial, sans-serif" }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {displayItems.map((item, i) => {
                            const amt = parseFloat(item.amount) ||
                                (parseFloat(item.qty) * parseFloat(item.unitPrice)) || null;
                            return (
                                <tr key={i} style={{ borderBottom: "0.5px solid #d1d5db", background: i % 2 === 0 ? "#fff" : "#f0fdf4" }}>
                                    <td style={{ padding: "4px 7px", textAlign: "right", fontSize: "10px", fontWeight: "600", borderRight: "0.5px solid #e5e7eb" }}>{item.qty ?? "\u00A0"}</td>
                                    <td style={{ padding: "4px 7px", textAlign: "center", fontSize: "9px", color: "#065f46", borderRight: "0.5px solid #e5e7eb" }}>{item.uom || "\u00A0"}</td>
                                    <td style={{ padding: "4px 7px", textAlign: "center", fontSize: "9px", color: "#374151", fontFamily: "monospace", borderRight: "0.5px solid #e5e7eb" }}>{item.itemCode || "\u00A0"}</td>
                                    <td style={{ padding: "4px 8px", fontSize: "10px", fontWeight: item.description ? "700" : "400", borderRight: "0.5px solid #e5e7eb" }}>{item.description || "\u00A0"}</td>
                                    <td style={{ padding: "4px 7px", textAlign: "right", fontSize: "10px", borderRight: "0.5px solid #e5e7eb" }}>{item.unitPrice ? fmt(item.unitPrice) : "\u00A0"}</td>
                                    <td style={{ padding: "4px 7px", textAlign: "right", fontSize: "10px", fontWeight: "700" }}>{amt ? fmt(amt) : "\u00A0"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "0 24px 10px" }}>
                <table style={{ borderCollapse: "collapse", fontSize: "10px", minWidth: "220px", fontFamily: "Arial, sans-serif" }}>
                    {[
                        ["TOTAL SALES:", fmt(totalSales), false],
                        ["DISCOUNT:", discount ? fmt(discount) : "—", false],
                        ["TOTAL AMOUNT PAYABLE:", fmt(totalPayable), true],
                    ].map(([label, val, bold]) => (
                        <tr key={label} style={bold ? { background: "#f0fdf4" } : {}}>
                            <td style={{ padding: "3px 10px 3px 0", color: bold ? "#166534" : "#555", fontWeight: "700", textAlign: "right", whiteSpace: "nowrap" }}>{label}</td>
                            <td style={{ padding: "3px 0", textAlign: "right", fontWeight: bold ? "900" : "400", color: bold ? "#166534" : "#000", fontSize: bold ? "12px" : "10px", borderBottom: bold ? "2px solid #166534" : "0.5px solid #e5e7eb", minWidth: "110px" }}>{val}</td>
                        </tr>
                    ))}
                </table>
            </div>
            <div style={{ padding: "0 24px 8px", display: "flex", gap: "24px", fontSize: "10px", color: "#555", fontFamily: "Arial, sans-serif" }}>
                <span><strong>Ref. #</strong> {f.refNo(data) || "—"}</span>
                <span><strong>SO #</strong>   {f.soRef(data) || "—"}</span>
            </div>
            <div style={{ display: "flex", padding: "8px 24px 14px", borderTop: "0.5px solid #e5e7eb", fontFamily: "Arial, sans-serif" }}>
                {[["PREPARED BY", f.preparedBy(data)], ["Confirmed by", f.checkedBy(data)], ["Received by", f.receivedBy(data)]].map(([label, val]) => (
                    <div key={label} style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ fontSize: "11px", fontWeight: "900", borderBottom: "1.5px solid #000", paddingBottom: "3px", minHeight: "26px", display: "inline-block", minWidth: "120px" }}>{val || "\u00A0"}</div>
                        <div style={{ fontSize: "7.5px", color: "#94a3b8", marginTop: "3px", fontStyle: "italic" }}>{label}:</div>
                    </div>
                ))}
            </div>
            <div style={{ padding: "6px 24px", borderTop: "0.5px solid #e5e7eb", background: "#f8fafc", textAlign: "center" }}>
                <p style={{ fontSize: "7px", color: "#94a3b8", fontStyle: "italic", margin: 0 }}>
                    This document is a property of Mega Soft Hygienic Products, Inc. Unauthorized reproduction is strictly prohibited.
                </p>
            </div>
        </div>
    );
};

// ── Scrap Form Picker ─────────────────────────────────────────────────────────
const ScrapPicker = ({ scrapRecords, selected, onToggle, search, onSearch }) => {
    return (
        <div className="flex flex-col h-full">
            <div className="relative mb-3">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="text" placeholder="Search control no, customer..."
                    value={search} onChange={e => onSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
            </div>
            <div className="flex-1 overflow-y-auto space-y-1.5 pr-1" style={{ maxHeight: "320px" }}>
                {scrapRecords.filter(r => {
                    const q = search.toLowerCase();
                    return !q || fScrap.controlNo(r).toLowerCase().includes(q) || fScrap.customerName(r).toLowerCase().includes(q);
                }).map(r => {
                    const ctrl = fScrap.controlNo(r);
                    const isSelected = selected.includes(ctrl);
                    return (
                        <div key={fScrap.id(r)}
                            onClick={() => onToggle(ctrl)}
                            className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${isSelected ? "border-blue-400 bg-blue-50" : "border-gray-100 bg-white hover:border-blue-200 hover:bg-blue-50/50"}`}>
                            <div className="mt-0.5 flex-shrink-0">
                                {isSelected
                                    ? <CheckSquare size={16} className="text-blue-600" />
                                    : <Square size={16} className="text-gray-300" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <span className="font-bold text-blue-700 font-mono text-xs">#{ctrl}</span>
                                    <span className="text-[10px] text-slate-400">{fmtDate(fScrap.formDate(r))}</span>
                                </div>
                                <p className="font-semibold text-slate-700 text-xs truncate">{fScrap.customerName(r)}</p>
                                <p className="text-[10px] text-slate-400">
                                    {fScrap.items(r).filter(i => i.scrapCodeName || i.ScrapCodeName).length} item(s) · Prepared by {fScrap.preparedBy(r) || "—"}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Item row editor ───────────────────────────────────────────────────────────
const ItemRow = ({ index, item, onChange, onRemove, scrapControlNo }) => {
    const qty = parseFloat(item.qty);
    const price = parseFloat(item.unitPrice);
    const computedAmt = !isNaN(qty) && !isNaN(price) ? qty * price : 0;

    return (
        <tr className={`border-b border-gray-100 ${index % 2 === 0 ? "bg-white" : "bg-slate-50/60"}`}>
            <td className="px-2 py-1.5 text-center text-slate-400 text-[10px] font-mono w-8">{index + 1}</td>
            {scrapControlNo && (
                <td className="px-2 py-1.5 text-center w-20">
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-100 text-blue-600 font-mono">{scrapControlNo}</span>
                </td>
            )}
            <td className="px-1.5 py-1.5 w-20">
                <input type="number" value={item.qty ?? ""} placeholder="0" min="0.001" step="0.001"
                    onChange={e => onChange("qty", e.target.value)}
                    className="w-full border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white font-semibold" />
            </td>
            <td className="px-1.5 py-1.5 w-20">
                <select value={item.uom || "KGS"} onChange={e => onChange("uom", e.target.value)}
                    className="w-full border border-gray-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white">
                    {UOM_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
            </td>
            <td className="px-1.5 py-1.5 w-28">
                <input type="text" value={item.itemCode ?? ""} placeholder="Item code"
                    onChange={e => onChange("itemCode", e.target.value)}
                    className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white font-mono" />
            </td>
            <td className="px-1.5 py-1.5">
                <input type="text" value={item.description ?? ""} placeholder="Description"
                    onChange={e => onChange("description", e.target.value)}
                    className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white font-semibold" />
            </td>
            <td className="px-1.5 py-1.5 w-24">
                <input type="number" value={item.unitPrice ?? ""} placeholder="0.00" min="0" step="0.0001"
                    onChange={e => {
                        const price = e.target.value;
                        const q = parseFloat(item.qty);
                        const p = parseFloat(price);
                        onChange("unitPrice", price);
                        if (!isNaN(q) && !isNaN(p)) {
                            onChange("amount", (q * p).toFixed(2));
                        }
                    }}
                    className="w-full border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" />
            </td>
            <td className="px-1.5 py-1.5 w-24">
                <input type="number" value={item.amount ?? (computedAmt || "")} placeholder="0.00" min="0" step="0.01"
                    onChange={e => onChange("amount", e.target.value)}
                    className="w-full border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white font-bold text-blue-700" />
            </td>
            <td className="px-1.5 py-1.5 w-10 text-center">
                <button onClick={onRemove} className="text-red-400 hover:text-red-600 transition-colors cursor-pointer">
                    <X size={13} />
                </button>
            </td>
        </tr>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════
const SalesOrder = () => {
    const [currentUser, setCurrentUser] = useState(null);
    useEffect(() => {
        const stored = localStorage.getItem("user");
        if (stored) { try { setCurrentUser(JSON.parse(stored)); } catch { } }
    }, []);
    const userName = currentUser?.fullName || currentUser?.name || currentUser?.username || "Unknown User";

    // ── State ─────────────────────────────────────────────────────────────────
    const [records, setRecords] = useState([]);
    const [scrapRecords, setScrapRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scrapLoading, setScrapLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formStep, setFormStep] = useState(1);
    const [formLoading, setFormLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [scrapSearch, setScrapSearch] = useState("");
    const [form, setForm] = useState(EMPTY_FORM());

    // ── Fetch SOs ─────────────────────────────────────────────────────────────
    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/SalesOrderScrap`);
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            setRecords(await res.json());
        } catch (err) {
            Swal.fire({ icon: "error", title: "Failed to load Sales Orders", text: err.message, confirmButtonColor: "#dc2626" });
        } finally { setLoading(false); }
    }, []);

    // ── Fetch Scrap Loading records ───────────────────────────────────────────
    const fetchScrapRecords = useCallback(async () => {
        setScrapLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/ScrapLoadingForm`);
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            setScrapRecords(await res.json());
        } catch (err) {
            Swal.fire({ icon: "error", title: "Failed to load Scrap Forms", text: err.message, confirmButtonColor: "#dc2626" });
        } finally { setScrapLoading(false); }
    }, []);

    useEffect(() => { fetchRecords(); fetchScrapRecords(); }, [fetchRecords, fetchScrapRecords]);

    // ── Next SO number ────────────────────────────────────────────────────────
    const generateSoNo = useCallback(() => {
        const max = records
            .map(r => parseInt(f.soNo(r), 10))
            .filter(n => !isNaN(n))
            .reduce((m, n) => Math.max(m, n), 0);
        return String(max + 1).padStart(6, "0");
    }, [records]);

    // ── Toggle scrap selection ────────────────────────────────────────────────
    const toggleScrapSelection = (controlNo) => {
        setForm(prev => {
            const already = prev.scrapControlNos.includes(controlNo);
            if (already) {
                return {
                    ...prev,
                    scrapControlNos: prev.scrapControlNos.filter(c => c !== controlNo),
                    items: prev.items.filter(i => i._srcControlNo !== controlNo),
                };
            }
            const scrap = scrapRecords.find(r => fScrap.controlNo(r) === controlNo);
            const newItems = scrap
                ? fScrap.items(scrap)
                    .filter(i => (i.scrapCodeName || i.ScrapCodeName) && (i.quantity || i.Quantity))
                    .map((i) => ({
                        _srcControlNo: controlNo,
                        qty: String(i.quantity || i.Quantity || ""),
                        uom: i.uom || i.Uom || i.UOM || "KGS",
                        itemCode: i.scrapCodeName || i.ScrapCodeName || "",
                        description: i.scrapCodeName || i.ScrapCodeName || "",
                        unitPrice: "",
                        amount: "",
                    }))
                : [];

            const orderedBy = prev.orderedBy ||
                (scrap ? fScrap.customerName(scrap) : "");

            return {
                ...prev,
                scrapControlNos: [...prev.scrapControlNos, controlNo],
                orderedBy,
                items: [...prev.items, ...newItems],
            };
        });
    };

    // ── Open new SO modal ─────────────────────────────────────────────────────
    const openNew = () => {
        setForm({ ...EMPTY_FORM(), soNo: generateSoNo(), preparedBy: userName });
        setFormStep(1);
        setScrapSearch("");
        setShowForm(true);
    };

    // ── Update item ───────────────────────────────────────────────────────────
    const updateItem = (idx, field, value) => {
        setForm(prev => {
            const items = [...prev.items];
            items[idx] = { ...items[idx], [field]: value };
            return { ...prev, items };
        });
    };
    const removeItem = (idx) => {
        setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
    };
    const addBlankItem = () => {
        setForm(prev => ({
            ...prev,
            items: [...prev.items, { _srcControlNo: null, qty: "", uom: "KGS", itemCode: "", description: "", unitPrice: "", amount: "" }],
        }));
    };

    // ── Submit — NO NULLS ─────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!form.orderedBy.trim())
            return Swal.fire({ icon: "warning", title: "Ordered By is required", confirmButtonColor: "#1d4ed8" });

        const validItems = form.items
            .filter(i => i.description || i.itemCode || i.qty)
            .map((item, idx) => ({
                lineNo: idx + 1,
                scrapControlNo: item._srcControlNo?.trim() || "",
                qty: parseFloat(item.qty) || 0,
                uom: item.uom?.trim() || "KGS",
                itemCode: item.itemCode?.trim() || "",
                description: item.description?.trim() || "",
                unitPrice: parseFloat(item.unitPrice) || 0,
                amount: parseFloat(item.amount) ||
                    (parseFloat(item.qty) * parseFloat(item.unitPrice)) || 0,
            }));

        if (validItems.length === 0)
            return Swal.fire({ icon: "warning", title: "No items", text: "Add at least one item.", confirmButtonColor: "#1d4ed8" });

        setFormLoading(true);
        try {
            const payload = {
                soNo: form.soNo,
                soPrefix: form.soPrefix?.trim() || "VS",
                soDate: form.soDate || new Date().toISOString().split("T")[0],
                orderedBy: form.orderedBy.trim(),
                address: form.address?.trim() || "",
                salesman: form.salesman?.trim() || "",
                refNo: form.refNo?.trim() || "",
                soRef: form.soRef?.trim() || "",
                remarks: form.remarks?.trim() || "",
                preparedBy: form.preparedBy?.trim() || "",
                checkedBy: form.checkedBy?.trim() || "",
                receivedBy: form.receivedBy?.trim() || "",
                createdBy: userName || "",
                scrapControlNos: form.scrapControlNos || [],
                items: validItems,
            };

            const res = await fetch(`${API_BASE_URL}api/SalesOrderScrap`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                throw new Error(e?.message || `Server error: ${res.status}`);
            }

            setShowForm(false);
            fetchRecords();

            // ✅ Log call OUTSIDE try-catch — hindi na makakaapekto sa success flow
            fetch(`${API_BASE_URL}api/Logs`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: "CREATE", module: "Sales Order",
                    description: `Created SO #${form.soNo}`,
                    userId: currentUser?.id,
                    username: currentUser?.username,
                    fullName: userName,
                }),
            }).catch(() => { }); // silent fail lang

            await Swal.fire({ icon: "success", title: "Sales Order Created!", timer: 2000, showConfirmButton: false });

        } catch (err) {
            Swal.fire({ icon: "error", title: "Save Failed", text: err.message, confirmButtonColor: "#dc2626" });
        } finally {
            setFormLoading(false);
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async (row) => {
        const id = f.id(row);
        const soNo = f.soNo(row);
        const { isConfirmed } = await Swal.fire({
            icon: "warning", title: "Delete Sales Order?",
            html: `Delete SO <strong>${f.soPrefix(row)}${soNo}</strong>?<br/>This cannot be undone.`,
            showCancelButton: true, confirmButtonText: "Yes, Delete", cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626", cancelButtonColor: "#6b7280",
        });
        if (!isConfirmed) return;
        try {
            const res = await fetch(`${API_BASE_URL}api/SalesOrderScrap/${id}`, { method: "DELETE" });
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            await Swal.fire({ icon: "success", title: "Deleted!", timer: 1800, showConfirmButton: false });
            setCurrentPage(1);
            fetchRecords();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Delete Failed", text: err.message, confirmButtonColor: "#dc2626" });
        }
    };

    // ── Filter + paginate ─────────────────────────────────────────────────────
    const filtered = useMemo(() =>
        records.filter(row => {
            const q = search.toLowerCase();
            return !q || f.soNo(row).toLowerCase().includes(q)
                || f.orderedBy(row).toLowerCase().includes(q)
                || f.preparedBy(row).toLowerCase().includes(q);
        }),
        [records, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const paginBtn = (d) => `px-3 py-1.5 rounded-md text-xs font-semibold border transition-all ${d ? "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50 cursor-pointer"}`;

    // ── Current form totals ───────────────────────────────────────────────────
    const { totalSales, totalPayable } = computeTotals(form.items);

    // ═══════════════════════════════════════════════════════════════════════════
    return (
        <div className="min-h-screen bg-slate-100 p-8 font-sans">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden w-full">

                {/* PAGE HEADER — BLUE */}
                <div style={gradBlue} className="px-8 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl">
                            <ShoppingCart className="text-white" size={22} />
                        </div>
                        <div>
                            <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">Mega Soft · Sales Management</p>
                            <h1 className="text-white font-extrabold text-xl leading-tight">Sales Orders</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-white/15 border border-white/20 rounded-lg px-3 py-1.5">
                            <Users size={14} className="text-white/70" />
                            <span className="text-white text-sm font-semibold">{userName}</span>
                        </div>
                        <button onClick={openNew}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold border-0 cursor-pointer transition-all hover:-translate-y-0.5"
                            style={{ background: "linear-gradient(135deg,#1e40af,#2563eb)", boxShadow: "0 2px 10px rgba(37,99,235,0.35)" }}>
                            <Plus size={16} /> New Sales Order
                        </button>
                    </div>
                </div>

                <div className="p-8">
                    {/* TOOLBAR */}
                    <div className="flex flex-wrap gap-3 mb-4 items-center">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input type="text" placeholder="Search SO no, customer, prepared by..."
                                value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                        </div>
                        <button onClick={fetchRecords} className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer">
                            <RefreshCw size={15} />
                        </button>
                    </div>

                    {!loading && <p className="text-xs text-slate-500 mb-3">{filtered.length} record{filtered.length !== 1 ? "s" : ""} found</p>}

                    {/* TABLE */}
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-slate-400 gap-3">
                            <RefreshCw size={20} className="animate-spin" />
                            <span className="text-sm font-medium">Loading Sales Orders...</span>
                        </div>
                    ) : paginated.length === 0 ? (
                        <div className="text-center py-16 text-slate-400 text-sm">No Sales Orders found.</div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="bg-blue-800 text-white">
                                        {["#", "SO No.", "Date", "Ordered By", "Salesman", "Total Payable", "Scrap Forms", "Prepared By", "Actions"].map((h, i, arr) => (
                                            <th key={i} className={`px-3 py-3 text-left text-xs font-bold uppercase tracking-wide whitespace-nowrap ${h === "Actions" || h === "Total Payable" ? "text-center" : ""} ${i === 0 ? "rounded-tl-xl" : ""} ${i === arr.length - 1 ? "rounded-tr-xl" : ""}`}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((row, idx) => {
                                        const { totalPayable: tp } = computeTotals(f.items(row));
                                        const refs = f.refs(row);
                                        return (
                                            <tr key={f.id(row) || idx} className={`border-b border-gray-100 transition-colors hover:bg-blue-50 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                                                <td className="px-3 py-3 text-center text-slate-400 text-xs w-10">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                                                <td className="px-3 py-3 font-bold text-blue-700 whitespace-nowrap font-mono">
                                                    <span className="text-blue-400">{f.soPrefix(row)}</span>{f.soNo(row) || "—"}
                                                </td>
                                                <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{fmtDate(f.soDate(row))}</td>
                                                <td className="px-3 py-3 text-slate-700 font-semibold">{f.orderedBy(row) || "—"}</td>
                                                <td className="px-3 py-3 text-slate-600">{f.salesman(row) || "—"}</td>
                                                <td className="px-3 py-3 text-center font-bold text-blue-700">{fmt(tp)}</td>
                                                <td className="px-3 py-3">
                                                    <div className="flex gap-1 flex-wrap">
                                                        {refs.map(ref => (
                                                            <span key={ref.scrapControlNo || ref.ScrapControlNo}
                                                                className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 font-mono">
                                                                #{ref.scrapControlNo || ref.ScrapControlNo}
                                                            </span>
                                                        ))}
                                                        {refs.length === 0 && <span className="text-slate-400 text-xs">—</span>}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3 text-slate-700">{f.preparedBy(row) || "—"}</td>
                                                <td className="px-3 py-3 text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button onClick={() => setSelectedRow(row)}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-semibold hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all cursor-pointer">
                                                            <Eye size={13} /> View
                                                        </button>
                                                        <button onClick={() => handleDelete(row)}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all cursor-pointer">
                                                            <Trash2 size={13} /> Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* PAGINATION */}
                    {!loading && filtered.length > PAGE_SIZE && (
                        <div className="flex justify-between items-center mt-5">
                            <span className="text-xs text-slate-500">Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                            <div className="flex gap-1.5 items-center">
                                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className={paginBtn(currentPage === 1)}>«</button>
                                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className={paginBtn(currentPage === 1)}>‹</button>
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let page;
                                    if (totalPages <= 5) page = i + 1;
                                    else if (currentPage <= 3) page = i + 1;
                                    else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                                    else page = currentPage - 2 + i;
                                    return (
                                        <button key={page} onClick={() => setCurrentPage(page)}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-all cursor-pointer ${page === currentPage ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
                                            {page}
                                        </button>
                                    );
                                })}
                                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className={paginBtn(currentPage === totalPages)}>›</button>
                                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className={paginBtn(currentPage === totalPages)}>»</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* PAGE FOOTER */}
                <div className="px-8 py-3 border-t border-gray-100 bg-slate-50 flex justify-between items-center">
                    <p className="text-[10px] text-slate-400 italic">This document is a property of Mega Soft. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.</p>
                    <span className="text-xs text-slate-400">{new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════
                NEW SO MODAL
                ══════════════════════════════════════════════════════ */}
            {showForm && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
                    <div className="bg-white rounded-3xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden"
                        style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.40)" }}>

                        {/* Modal header — BLUE */}
                        <div style={gradBlue} className="px-7 py-5 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><ShoppingCart size={20} className="text-white" /></div>
                                <div>
                                    <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em]">
                                        {formStep === 1 ? "Step 1 of 2 · Select Scrap Forms" : "Step 2 of 2 · Sales Order Details"}
                                    </p>
                                    <h2 className="text-white font-extrabold text-base">New Sales Order</h2>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-right">
                                    <p className="text-blue-300 text-[9px] font-bold uppercase tracking-widest">SO No.</p>
                                    <p className="text-white font-extrabold text-sm tracking-widest font-mono">
                                        <span className="text-blue-300">VS</span>{form.soNo}
                                    </p>
                                </div>
                                <button onClick={() => setShowForm(false)} className="bg-white/15 hover:bg-white/25 text-white rounded-xl w-9 h-9 flex items-center justify-center cursor-pointer transition-all" style={{ border: "none" }}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Step indicator */}
                        <div className="flex bg-slate-800 flex-shrink-0">
                            {[
                                { n: 1, label: "Select Scrap Forms", icon: ClipboardList },
                                { n: 2, label: "SO Details & Items", icon: FileText },
                            ].map(({ n, label, icon: Icon }) => (
                                <div key={n}
                                    onClick={() => n < formStep && setFormStep(n)}
                                    className={`flex items-center gap-2 px-6 py-3 text-xs font-bold transition-all ${formStep === n ? "border-b-2 border-blue-400 text-blue-300" : "text-slate-400"} ${n < formStep ? "cursor-pointer hover:text-blue-300" : "cursor-default"}`}>
                                    <Icon size={13} /> {label}
                                    {n === 1 && form.scrapControlNos.length > 0 && (
                                        <span className="ml-1 bg-blue-500 text-white rounded-full px-1.5 py-0.5 text-[9px] font-bold">{form.scrapControlNos.length}</span>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* ── STEP 1 ─────────────────────────────────────────── */}
                        {formStep === 1 && (
                            <div className="flex-1 overflow-y-auto p-7 bg-slate-50">
                                <div className="bg-white rounded-2xl border border-gray-100 p-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Select Scrap Loading Forms</h3>
                                            <p className="text-xs text-slate-500 mt-0.5">Pick one or multiple Scrap Loading records to include in this Sales Order.</p>
                                        </div>
                                        {form.scrapControlNos.length > 0 && (
                                            <div className="flex gap-1 flex-wrap justify-end">
                                                {form.scrapControlNos.map(c => (
                                                    <span key={c} className="px-2 py-1 rounded-lg text-[10px] font-bold bg-blue-100 text-blue-700 font-mono">#{c}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {scrapLoading ? (
                                        <div className="flex items-center justify-center py-8 text-slate-400 gap-2">
                                            <RefreshCw size={16} className="animate-spin" /> Loading scrap forms...
                                        </div>
                                    ) : (
                                        <ScrapPicker
                                            scrapRecords={scrapRecords}
                                            selected={form.scrapControlNos}
                                            onToggle={toggleScrapSelection}
                                            search={scrapSearch}
                                            onSearch={setScrapSearch}
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── STEP 2 ─────────────────────────────────────────── */}
                        {formStep === 2 && (
                            <div className="flex-1 overflow-y-auto p-7 bg-slate-50 space-y-5">

                                {/* Header info */}
                                <div className="bg-white rounded-2xl border border-gray-100 p-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Sales Order Information</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">SO No.</label>
                                            <div className="w-full border-2 border-blue-100 bg-blue-50 rounded-xl px-3 py-2.5 text-sm font-extrabold text-blue-700 tracking-widest select-all font-mono">
                                                <span className="text-blue-400">VS</span>{form.soNo}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Date</label>
                                            <input type="date" value={form.soDate} onChange={e => setForm(p => ({ ...p, soDate: e.target.value }))}
                                                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Ordered By (Customer)</label>
                                            <input type="text" value={form.orderedBy} placeholder="Customer name"
                                                onChange={e => setForm(p => ({ ...p, orderedBy: e.target.value }))}
                                                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-semibold" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Address</label>
                                            <input type="text" value={form.address} placeholder="Customer address"
                                                onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                                                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Salesman</label>
                                            <input type="text" value={form.salesman} placeholder="Salesman name"
                                                onChange={e => setForm(p => ({ ...p, salesman: e.target.value }))}
                                                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Ref. #</label>
                                            <input type="text" value={form.refNo} placeholder="e.g. 1372257"
                                                onChange={e => setForm(p => ({ ...p, refNo: e.target.value }))}
                                                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-mono" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">SO Ref #</label>
                                            <input type="text" value={form.soRef} placeholder="e.g. 7930 7931"
                                                onChange={e => setForm(p => ({ ...p, soRef: e.target.value }))}
                                                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-mono" />
                                        </div>
                                    </div>
                                </div>

                                {/* Items */}
                                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                    <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                                        <div>
                                            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Line Items</h3>
                                            {form.scrapControlNos.length > 0 && (
                                                <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                                                    <Link2 size={10} />
                                                    Pulled from: {form.scrapControlNos.map(c => `#${c}`).join(", ")}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1">
                                                Total: ₱{fmt(totalPayable)}
                                            </span>
                                            <button onClick={addBlankItem}
                                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold text-blue-700 cursor-pointer transition-all hover:bg-blue-100"
                                                style={{ background: "#dbeafe", border: "1.5px solid #93c5fd" }}>
                                                <Plus size={12} /> Add Row
                                            </button>
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm border-collapse">
                                            <thead>
                                                <tr className="bg-blue-800 text-white">
                                                    <th className="px-2 py-2 text-[10px] font-bold uppercase text-center w-8">#</th>
                                                    {form.scrapControlNos.length > 0 && (
                                                        <th className="px-2 py-2 text-[10px] font-bold uppercase text-center w-20">Source</th>
                                                    )}
                                                    <th className="px-2 py-2 text-[10px] font-bold uppercase text-right w-20">Qty</th>
                                                    <th className="px-2 py-2 text-[10px] font-bold uppercase text-center w-20">UOM</th>
                                                    <th className="px-2 py-2 text-[10px] font-bold uppercase text-left w-28">Item Code</th>
                                                    <th className="px-2 py-2 text-[10px] font-bold uppercase text-left">Description</th>
                                                    <th className="px-2 py-2 text-[10px] font-bold uppercase text-right w-24">Unit Price</th>
                                                    <th className="px-2 py-2 text-[10px] font-bold uppercase text-right w-24">Amount</th>
                                                    <th className="px-2 py-2 w-10"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {form.items.length === 0 ? (
                                                    <tr><td colSpan={form.scrapControlNos.length > 0 ? 9 : 8} className="py-8 text-center text-slate-400 text-xs">No items yet. Go back to Step 1 or add rows manually.</td></tr>
                                                ) : form.items.map((item, idx) => (
                                                    <ItemRow
                                                        key={idx}
                                                        index={idx}
                                                        item={item}
                                                        scrapControlNo={form.scrapControlNos.length > 0 ? item._srcControlNo : null}
                                                        onChange={(field, value) => updateItem(idx, field, value)}
                                                        onRemove={() => removeItem(idx)}
                                                    />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Closing info */}
                                <div className="bg-white rounded-2xl border border-gray-100 p-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Closing Information</h3>
                                    <div className="mb-4">
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Remarks</label>
                                        <textarea value={form.remarks} rows={2} placeholder="Optional remarks..."
                                            onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none" />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {[
                                            { key: "preparedBy", label: "Prepared By" },
                                            { key: "checkedBy", label: "Confirmed By" },
                                            { key: "receivedBy", label: "Received By" },
                                        ].map(({ key, label }) => (
                                            <div key={key}>
                                                <label className="block text-xs font-bold text-slate-600 mb-1.5">{label}</label>
                                                <input type="text" value={form[key]} placeholder={`${label} name...`}
                                                    onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                                                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Modal footer */}
                        <div className="px-7 py-4 bg-white border-t border-gray-100 flex items-center justify-between flex-shrink-0"
                            style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}>
                            <button onClick={() => setShowForm(false)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer">
                                <X size={14} /> Cancel
                            </button>
                            <div className="flex items-center gap-3">
                                {formStep === 2 && (
                                    <button onClick={() => setFormStep(1)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer">
                                        ← Back
                                    </button>
                                )}
                                {formStep === 1 ? (
                                    <button onClick={() => setFormStep(2)}
                                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-bold cursor-pointer transition-all hover:-translate-y-0.5"
                                        style={{ background: "linear-gradient(135deg,#1e40af,#2563eb)", boxShadow: "0 2px 10px rgba(37,99,235,0.35)", border: "none" }}>
                                        Next: SO Details →
                                    </button>
                                ) : (
                                    <button onClick={handleSubmit} disabled={formLoading}
                                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-bold cursor-pointer disabled:opacity-50 transition-all hover:-translate-y-0.5"
                                        style={{ background: "linear-gradient(135deg,#1e40af,#2563eb)", boxShadow: "0 2px 10px rgba(37,99,235,0.35)", border: "none" }}>
                                        <Save size={15} />
                                        {formLoading ? "Saving..." : "Save Sales Order"}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ══ VIEW MODAL ══ */}
            {selectedRow && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={e => { if (e.target === e.currentTarget) setSelectedRow(null); }}>
                    <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden"
                        style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.40)" }}>

                        <div style={gradBlue} className="px-7 py-5 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><ShoppingCart size={20} className="text-white" /></div>
                                <div>
                                    <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">Mega Soft · Sales Management</p>
                                    <h2 className="text-white font-extrabold text-base leading-none">Sales Order</h2>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="text-right bg-white/10 border border-white/20 rounded-xl px-4 py-2">
                                    <p className="text-blue-300 text-[9px] font-bold uppercase tracking-widest">SO No.</p>
                                    <p className="text-white font-extrabold text-sm tracking-wider font-mono">
                                        <span className="text-blue-300">{f.soPrefix(selectedRow)}</span>{f.soNo(selectedRow)}
                                    </p>
                                </div>
                                <button onClick={() => setSelectedRow(null)} className="bg-white/15 hover:bg-white/25 text-white rounded-xl w-9 h-9 flex items-center justify-center transition-all cursor-pointer" style={{ border: "none" }}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Meta bar — BLUE */}
                        <div className="px-7 py-2.5 flex items-center gap-1 flex-wrap flex-shrink-0" style={{ background: "rgba(30,58,138,0.93)" }}>
                            {[
                                { icon: CalendarDays, label: "Date", value: fmtDate(f.soDate(selectedRow)) },
                                { icon: Users, label: "Customer", value: f.orderedBy(selectedRow) || "—" },
                                { icon: Building2, label: "Prepared By", value: f.preparedBy(selectedRow) || "—" },
                            ].map((item, i) => {
                                const Icon = item.icon;
                                return (
                                    <React.Fragment key={i}>
                                        {i > 0 && <span className="w-px h-4 bg-white/20 mx-3" />}
                                        <span className="flex items-center gap-2 text-xs">
                                            <Icon size={12} className="text-blue-400" />
                                            <span className="text-white/50">{item.label}:</span>
                                            <span className="text-white font-semibold">{item.value}</span>
                                        </span>
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        <div className="flex-1 overflow-y-auto" style={{ background: "#e8edf5" }}>
                            <div className="p-6">
                                <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
                                    <SalesOrderDocument data={selectedRow} />
                                </div>
                            </div>
                        </div>

                        <div className="px-7 py-4 bg-white border-t border-gray-100 flex items-center justify-between flex-shrink-0"
                            style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}>
                            <button onClick={async () => {
                                setPdfLoading(true);
                                try { await exportSOPDF(selectedRow); }
                                catch (err) { Swal.fire({ icon: "error", title: "PDF Export Failed", text: err.message, confirmButtonColor: "#dc2626" }); }
                                finally { setPdfLoading(false); }
                            }} disabled={pdfLoading}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-all cursor-pointer hover:-translate-y-0.5"
                                style={{ background: "linear-gradient(135deg,#dc2626,#b91c1c)", boxShadow: "0 2px 10px rgba(220,38,38,0.35)", border: "none" }}>
                                <Download size={15} />
                                {pdfLoading ? "Generating PDF..." : "Export PDF"}
                            </button>
                            <button onClick={() => setSelectedRow(null)}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer">
                                <X size={14} /> Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesOrder;